import React, { useState, useEffect, useRef, Suspense, lazy, Component } from "react";
import { Spinner } from "react-bootstrap";

// Lazy so each viewer stays out of the main bundle until its format is opened.
const PdfViewer = lazy(() => import("./PdfViewer"));
const PptxViewer = lazy(() => import("./PptxViewer"));

/*
 * The file renderer, with no chrome of its own. Copied from the Teams file
 * manager so a client sees a document exactly as the ADM Team does; keep the
 * two in step when either changes.
 *
 * Browsers cannot render Office documents natively, so instead of handing them
 * to a third-party viewer we render them here in the browser. Nothing leaves
 * our own infrastructure.
 *
 *   - Spreadsheets go to @js-preview/excel (ExcelJS + x-spreadsheet), so fonts,
 *     fills, borders, merges and number formats come from the library rather
 *     than from any mapping of our own.
 *   - Word documents go to docx-preview, which reproduces Word's page layout,
 *     styles, headers, footers and numbering.
 *   - Slide decks go to pptxviewjs, which paints each slide to a canvas.
 *     Slides are the least faithful: fonts, SmartArt and vector (EMF/WMF)
 *     images are the usual casualties.
 *   - CSV has no styling to preserve, so SheetJS renders it as a plain table.
 */

const EXCEL_EXTENSIONS = ["xlsx", "xlsm", "xls", "csv"];
const WORD_EXTENSIONS = ["docx"]; // docx-preview reads .docx only, not legacy .doc
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif"];

// Uses the MIME type where we have a useful one, and falls back to the
// extension for the older formats our mime map reports as octet-stream.
export const officeKindOf = (fileType = "", fileName = "") => {
  const type = (fileType || "").toLowerCase();
  const extension = (fileName || "").split(".").pop().toLowerCase();

  if (
    type.includes("spreadsheetml") ||
    type === "application/vnd.ms-excel" ||
    type === "text/csv" ||
    EXCEL_EXTENSIONS.includes(extension)
  ) {
    return "excel";
  }
  if (type.includes("wordprocessingml") || WORD_EXTENSIONS.includes(extension)) {
    return "word";
  }
  return null;
};

// True for anything this component can display, so callers know when to open a
// preview instead of handing the file to a browser tab.
export const canPreviewInModal = (fileType = "", fileName = "") => {
  const type = (fileType || "").toLowerCase();
  const extension = (fileName || "").split(".").pop().toLowerCase();
  if (officeKindOf(type, fileName)) return true;
  if (type === "application/pdf" || extension === "pdf") return true;
  if (type.includes("presentationml") || extension === "pptx") return true;
  if (type.startsWith("image/") || IMAGE_EXTENSIONS.includes(extension)) return true;
  return false;
};

// Styling for the sandboxed iframe, which only the CSV view uses. Scoped in
// there, so it cannot collide with Bootstrap or leak into the admin UI.
const VIEWER_CSS = `
  body {
    margin: 0;
    padding: 16px;
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    font-size: 13px;
    color: #212529;
    background: #fff;
  }
  table { border-collapse: collapse; width: max-content; min-width: 100%; }
  td, th {
    border: 1px solid #dee2e6;
    padding: 4px 8px;
    white-space: nowrap;
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  th { background: #f8f9fa; font-weight: 600; }
`;

/*
 * Strips the privileges from any iframe a viewer library leaves in our DOM.
 *
 * An iframe with no sandbox attribute is same-origin with the admin app: script
 * inside it reaches document.cookie, parent.document and the API session. An
 * empty sandbox attribute denies every capability - no scripts, and a unique
 * opaque origin - which is what untrusted document content should get.
 */
const hardenIframes = (container) => {
  if (!container) return;
  container.querySelectorAll("iframe").forEach((frame) => {
    if (frame.getAttribute("sandbox") === "") return;
    frame.setAttribute("sandbox", "");

    // The sandbox is only applied when the frame navigates, so a frame that
    // already holds a document has to be re-pointed at its own source. An
    // altChunk frame carries srcdoc and an empty src - assigning that empty
    // src back would send it to this page's own URL instead of reloading it.
    const srcdoc = frame.getAttribute("srcdoc");
    if (srcdoc !== null) {
      frame.setAttribute("srcdoc", srcdoc);
    } else if (frame.getAttribute("src")) {
      // eslint-disable-next-line no-self-assign
      frame.src = frame.src;
    }
  });
};

/*
 * Cell values are written into the iframe's markup as text, so every character
 * the parser hands back has to be inert. The frame is sandboxed with no
 * allow-scripts, which already stops a payload running - this is what keeps a
 * cell containing "<b>" rendering as those four characters rather than as
 * markup that mangles the table.
 */
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/*
 * Catches a viewer that fails to load or throws while rendering.
 *
 * Suspense covers the waiting, not the failing, and this app has no error
 * boundary of its own - so without this a viewer that throws unmounts the whole
 * page rather than just the preview. The case that actually happens is a stale
 * chunk: the viewers are code-split, and a session that was open across a
 * deploy asks for a chunk filename the new build no longer has.
 *
 * Reloading the page is the real fix for that, so the message offers it, and
 * the file stays downloadable either way.
 */
class ViewerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error("File preview viewer failed:", error);
  }

  componentDidUpdate(prevProps) {
    // A new file gets a fresh attempt; without this the panel would stay broken
    // for the rest of the session once any one file had failed.
    if (prevProps.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="text-center py-4">
        <p className="text-muted mb-2">
          This viewer could not be loaded. You can still download the file.
        </p>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => window.location.reload()}
        >
          Reload the page
        </button>
      </div>
    );
  }
}

const loadingBlock = (
  <div className="text-center text-muted py-5">
    <Spinner animation="border" size="sm" className="me-2" />
    Loading viewer...
  </div>
);

const FilePreview = ({ fileUrl, fileType, fileName, height = 600 }) => {
  const [frameHtml, setFrameHtml] = useState(""); // CSV, shown in an iframe
  const [officeLoading, setOfficeLoading] = useState(false);
  const [officeError, setOfficeError] = useState(null);
  const [officeErrorDetail, setOfficeErrorDetail] = useState(null);

  const containerRef = useRef(null);
  const instanceRef = useRef(null);

  const safeType = fileType || "";
  const officeKind = officeKindOf(safeType, fileName);
  // CSV is plain text with no formatting, so it takes the simple path.
  const isCsv =
    safeType.toLowerCase().includes("csv") ||
    (fileName || "").toLowerCase().endsWith(".csv");
  const isSpreadsheet = officeKind === "excel" && !isCsv;
  const isPptx =
    safeType.toLowerCase().includes("presentationml") ||
    (fileName || "").toLowerCase().endsWith(".pptx");
  const isImage =
    safeType.startsWith("image/") ||
    IMAGE_EXTENSIONS.includes((fileName || "").split(".").pop().toLowerCase());
  const isPdf = safeType === "application/pdf";
  // Spreadsheets and Word documents are drawn by a library into a container we
  // give it; only CSV still goes through the iframe route.
  const usesLibrary = isSpreadsheet || officeKind === "word";

  /* Spreadsheets and Word documents: hand the blob straight to the library */
  useEffect(() => {
    if (!fileUrl || !usesLibrary) return undefined;

    let cancelled = false;
    setOfficeError(null);
    setOfficeErrorDetail(null);
    setOfficeLoading(true);

    (async () => {
      try {
        // Static import() paths on each branch so both libraries land in their
        // own chunk and only the one actually needed gets fetched.
        if (isSpreadsheet) {
          const [module] = await Promise.all([
            import("@js-preview/excel"),
            import("@js-preview/excel/lib/index.css"),
          ]);
          if (cancelled || !containerRef.current) return;

          const previewer = module.default.init(containerRef.current);
          instanceRef.current = previewer;

          // fileUrl is an object URL for a blob we already fetched, so this
          // reads it back out of memory rather than hitting the network again.
          const response = await fetch(fileUrl);
          const blob = await response.blob();
          if (cancelled) return;

          await previewer.preview(blob);
        } else {
          const { renderAsync } = await import("docx-preview");
          if (cancelled || !containerRef.current) return;

          const response = await fetch(fileUrl);
          const blob = await response.blob();
          if (cancelled) return;

          // docx-preview injects its own styles and paints the pages itself.
          await renderAsync(blob, containerRef.current, null, {
            inWrapper: true,
            breakPages: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            // MUST stay false. A .docx can embed an altChunk part holding raw
            // HTML, and docx-preview renders one by creating an <iframe> with
            // no sandbox and assigning the part to srcdoc - same origin with
            // this app, so script inside it can read the session cookie and
            // call the API as the signed-in user. The library defaults this
            // to true; a client-uploaded document is not trusted input.
            renderAltChunks: false,
          });

          // Belt and braces: renderAltChunks above is what stops the iframe
          // being created, but if a future version of the library grows
          // another path to one, it gets sandboxed rather than trusted.
          hardenIframes(containerRef.current);
        }
      } catch (err) {
        console.error("Failed to render document preview:", err);
        if (!cancelled) {
          setOfficeError(
            "This document could not be displayed. It may be password protected, " +
              "in an older format, or corrupted. You can still download it."
          );
          setOfficeErrorDetail(err && err.message ? String(err.message) : String(err));
        }
      } finally {
        if (!cancelled) setOfficeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // The spreadsheet library owns its instance; docx-preview just leaves
      // its markup behind, so that container is emptied by hand.
      if (instanceRef.current) {
        try {
          instanceRef.current.destroy();
        } catch (err) {
          console.warn("Document preview cleanup failed:", err);
        }
        instanceRef.current = null;
      } else if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [fileUrl, usesLibrary, isSpreadsheet, height]);

  /* CSV: convert to a plain table and show it in a sandboxed iframe */
  useEffect(() => {
    if (!fileUrl || !officeKind || usesLibrary) return undefined;

    let cancelled = false;
    setFrameHtml("");
    setOfficeError(null);
    setOfficeErrorDetail(null);
    setOfficeLoading(true);

    (async () => {
      try {
        const response = await fetch(fileUrl);
        const buffer = await response.arrayBuffer();
        // Decoded as text rather than raw bytes so accents survive.
        const text = new TextDecoder("utf-8").decode(buffer);

        const { default: Papa } = await import("papaparse");
        const { data, errors } = Papa.parse(text, {
          skipEmptyLines: "greedy",
          // Let the parser work out comma vs semicolon vs tab itself - European
          // exports and TSVs both turn up here.
          delimiter: "",
        });
        if (cancelled) return;

        // Papa reports malformed rows without throwing, and still returns
        // everything it could read. A partial table beats no preview at all,
        // so this is logged rather than surfaced.
        //
        // UndetectableDelimiter is filtered out: a single-column file gives the
        // parser nothing to detect, so it reports this and correctly defaults to
        // a comma. Left in, it would warn on every one-column CSV.
        const realErrors = errors.filter((e) => e.code !== "UndetectableDelimiter");
        if (realErrors.length) {
          console.warn(`CSV parsed with ${realErrors.length} issue(s):`, realErrors.slice(0, 3));
        }

        const body = data.length
          ? `<table>${data
              .map(
                (row, index) =>
                  `<tr>${row
                    .map((cell) => {
                      const tag = index === 0 ? "th" : "td";
                      return `<${tag}>${escapeHtml(cell)}</${tag}>`;
                    })
                    .join("")}</tr>`
              )
              .join("")}</table>`
          : "";

        setFrameHtml(
          `<!doctype html><html><head><meta charset="utf-8"><style>${VIEWER_CSS}</style></head><body>${body}</body></html>`
        );
      } catch (err) {
        console.error("Failed to render CSV preview:", err);
        if (!cancelled) {
          setOfficeError("This file could not be displayed. You can still download it.");
        }
      } finally {
        if (!cancelled) setOfficeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fileUrl, officeKind, usesLibrary]);

  if (!fileUrl) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
        <h5>File not found</h5>
      </div>
    );
  }

  return (
    <>
      {isImage && (
        <div
          style={{
            height: `${height}px`,
            overflow: "auto",
            textAlign: "center",
            background: "#f1f3f5",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
          }}
        >
          <img src={fileUrl} alt="Preview" style={{ maxWidth: "100%", height: "auto" }} />
        </div>
      )}

      {isPptx && (
        <ViewerErrorBoundary resetKey={fileUrl}>
          <Suspense fallback={loadingBlock}>
            <PptxViewer fileUrl={fileUrl} height={height} />
          </Suspense>
        </ViewerErrorBoundary>
      )}

      {isPdf && (
        <ViewerErrorBoundary resetKey={fileUrl}>
          <Suspense fallback={loadingBlock}>
            <PdfViewer fileUrl={fileUrl} height={height} />
          </Suspense>
        </ViewerErrorBoundary>
      )}

      {officeError && (
        <div className="text-center py-4">
          <p className="text-muted mb-1">{officeError}</p>
          {officeErrorDetail && (
            <code className="small text-danger" style={{ wordBreak: "break-word" }}>
              {officeErrorDetail}
            </code>
          )}
        </div>
      )}

      {officeLoading && !officeError && (
        <div className="text-center text-muted py-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Preparing preview...
        </div>
      )}

      {/* The library draws into this container itself, so it has to stay
          mounted while the effect above runs. */}
      {usesLibrary && (
        <div
          ref={containerRef}
          style={{
            width: "100%",
            height: `${height}px`,
            overflowY: isSpreadsheet ? "hidden" : "auto",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            background: isSpreadsheet ? "#fff" : "#f1f3f5",
            display: officeError ? "none" : "block",
          }}
        />
      )}

      {/* CSV, sandboxed so the file's own content can never run script or
          restyle the admin UI around it. */}
      {officeKind && !usesLibrary && !officeError && frameHtml && (
        <iframe
          sandbox=""
          srcDoc={frameHtml}
          title="Document Preview"
          style={{
            width: "100%",
            height: `${height}px`,
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            background: "#fff",
          }}
        />
      )}

      {!isImage && !isPdf && !isPptx && !officeKind && (
        <p className="text-center text-muted py-4">
          Preview not supported for this file type. You can download it instead.
        </p>
      )}
    </>
  );
};

export default FilePreview;
